import React from "react";

export type FeatureTab = "information" | "operations";

type FeatureTabsProps = {
  activeTab: FeatureTab;
  onChange: (tab: FeatureTab) => void;
};

const tabs: Array<{ id: FeatureTab; label: string }> = [
  { id: "information", label: "Information" },
  { id: "operations", label: "Operations" },
];

const FeatureTabs = ({ activeTab, onChange }: FeatureTabsProps) => {
  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, tabId: FeatureTab) => {
    const currentIndex = tabs.findIndex((tab) => tab.id === tabId);
    const nextIndex = event.key === "ArrowRight"
      ? (currentIndex + 1) % tabs.length
      : event.key === "ArrowLeft"
        ? (currentIndex - 1 + tabs.length) % tabs.length
        : event.key === "Home"
          ? 0
          : event.key === "End"
            ? tabs.length - 1
            : -1;

    if (nextIndex < 0) return;

    event.preventDefault();
    const nextTab = tabs[nextIndex];
    onChange(nextTab.id);
    document.getElementById(`feature-tab-${nextTab.id}`)?.focus();
  };

  return (
    <nav className="feature-tabs sticky top-0 z-20 isolate border-b border-slate-300 bg-[var(--workspace-canvas)]" aria-label="Feature sections">
      <div className="flex items-center gap-4" role="tablist">
        {tabs.map((tab) => {
          const isActive = activeTab === tab.id;

          return (
            <button
              key={tab.id}
              id={`feature-tab-${tab.id}`}
              type="button"
              role="tab"
              aria-selected={isActive}
              // Only the active panel is always in the DOM, some pages unmount the other one
              aria-controls={isActive ? `feature-panel-${tab.id}` : undefined}
              tabIndex={isActive ? 0 : -1}
              onClick={() => onChange(tab.id)}
              onKeyDown={(event) => handleKeyDown(event, tab.id)}
              className={`border-b-2 px-0 pb-2 pt-0 text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-slate-500 ${
                isActive
                  ? "border-[#1976d2] font-semibold text-[#145da8]"
                  : "border-transparent font-medium text-slate-700 hover:border-slate-300 hover:text-slate-900"
              }`}
            >
              {tab.label}
            </button>
          );
        })}
      </div>
    </nav>
  );
};

export default FeatureTabs;
